import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { api, qs } from "@/lib/api";
import { BRANDS } from "@/lib/brands";
import { formatCents, formatDateTime } from "@/lib/format";
import { useIsAdmin } from "@/lib/session";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { inDispute, ORDER_SOURCE, ORDER_STATUS, PAYMENT_STATUS, type OrderRow, type PaymentRow } from "./orders";
import { DisputeBadge, PaidWith } from "./PaymentBits";
import { RefundDialog } from "./RefundDialog";
import { useContactNames } from "./useContactNames";

/** Billing → Orders and payments. */
export function OrdersPage() {
  const { tab = "orders" } = useSearch({ from: "/billing/orders" });
  const navigate = useNavigate({ from: "/billing/orders" });
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <PageHeader title="Orders and payments" sub="Everything bought, and every card charge">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          An order is what someone bought; payments are the charges against its invoices (one for a single payment, several for a payment plan or subscription). Refunds start
          from a payment. Times are Mountain Time.
        </p>
      </PageHeader>
      <Tabs value={tab} onValueChange={(t) => navigate({ search: { tab: t === "orders" ? undefined : (t as "payments") }, replace: true })}>
        <TabsList>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="pt-3">
          <OrdersTab />
        </TabsContent>
        <TabsContent value="payments" className="pt-3">
          <PaymentsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OrdersTab() {
  const [brand, setBrand] = useState<number | "">("");
  const [status, setStatus] = useState("");
  const q = useInfiniteQuery({
    queryKey: ["orders", brand, status],
    queryFn: ({ pageParam }) => api<{ orders: OrderRow[]; next_before_id?: number }>("/api/v1/orders" + qs({ brand_id: brand || undefined, status: status || undefined, before_id: pageParam || undefined, limit: 100 })),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_before_id || undefined,
  });
  const rows = q.data?.pages.flatMap((p) => p.orders) ?? [];
  const name = useContactNames(rows.map((o) => o.contact_id));
  return (
    <Card className="overflow-x-auto">
      <div className="flex flex-wrap items-center gap-2 border-b p-3">
        <Select className="h-8 w-auto" value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")} aria-label="Brand">
          <option value="">All brands</option>
          {BRANDS.map((b) => (
            <option key={b.id} value={b.id}>
              {b.short}
            </option>
          ))}
        </Select>
        <Select className="h-8 w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">Any status</option>
          {Object.entries(ORDER_STATUS).map(([k, [label]]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </Select>
      </div>
      {q.isLoading && <Skeleton className="m-3 h-32" />}
      {!q.isLoading && rows.length === 0 && <Empty title="No orders match." />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Order</TH>
              <TH>Person</TH>
              <TH>Brand</TH>
              <TH>Placed (MT)</TH>
              <TH className="text-right">Total</TH>
              <TH>Status</TH>
            </tr>
          </THead>
          <tbody>
            {rows.map((o) => {
              const [label, variant] = ORDER_STATUS[o.status] ?? [o.status, "muted" as const];
              return (
                <TR key={o.id}>
                  <TD>
                    <Link to="/billing/orders/$orderId" params={{ orderId: String(o.id) }} className="num font-medium text-[var(--color-primary)] hover:underline">
                      #{o.id}
                    </Link>
                    <div className="text-xs text-[var(--color-muted-foreground)]">{ORDER_SOURCE[o.source] ?? o.source}</div>
                  </TD>
                  <TD className="max-w-[14rem] truncate">
                    <Link to="/contacts/$contactId" params={{ contactId: String(o.contact_id) }} className="hover:underline">
                      {name(o.contact_id)}
                    </Link>
                  </TD>
                  <TD>
                    <BrandMark id={o.brand_id} />
                  </TD>
                  <TD className="num whitespace-nowrap text-xs">{formatDateTime(o.placed_at)}</TD>
                  <TD className="num text-right">
                    {formatCents(o.total_cents)}
                    {o.discount_cents ? <div className="text-xs text-[var(--color-muted-foreground)]">{formatCents(o.discount_cents)} off</div> : null}
                  </TD>
                  <TD>
                    <Badge variant={variant}>{label}</Badge>
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="border-t p-2 text-center">
          <Button size="sm" variant="outline" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            Show more
          </Button>
        </div>
      )}
    </Card>
  );
}

function PaymentsTab() {
  const isAdmin = useIsAdmin();
  const [status, setStatus] = useState("");
  const [since, setSince] = useState("");
  const [refunding, setRefunding] = useState<PaymentRow | null>(null);
  const q = useInfiniteQuery({
    queryKey: ["payments", status, since],
    queryFn: ({ pageParam }) =>
      api<{ payments: PaymentRow[]; next_before_id?: number }>("/api/v1/payments" + qs({ status: status || undefined, since: since || undefined, before_id: pageParam || undefined, limit: 100 })),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_before_id || undefined,
  });
  const rows = q.data?.pages.flatMap((p) => p.payments) ?? [];
  const name = useContactNames(rows.map((p) => p.contact_id));
  return (
    <Card className="overflow-x-auto">
      <div className="flex flex-wrap items-center gap-2 border-b p-3">
        <Select className="h-8 w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Result">
          <option value="">Any result</option>
          {Object.entries(PAYMENT_STATUS).map(([k, [label]]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-1.5 text-sm">
          Since
          <Input className="h-8 w-auto" type="date" value={since} onChange={(e) => setSince(e.target.value)} aria-label="Since (Mountain Time)" />
        </label>
        {!isAdmin && <span className="ml-auto text-xs text-[var(--color-warning-fg)]">Only an admin can refund.</span>}
      </div>
      {q.isLoading && <Skeleton className="m-3 h-32" />}
      {!q.isLoading && rows.length === 0 && <Empty title="No payments match." />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>When (MT)</TH>
              <TH>Person</TH>
              <TH className="text-right">Amount</TH>
              <TH>Paid with</TH>
              <TH>Result</TH>
              <TH>Order</TH>
              {isAdmin && <TH className="w-px" />}
            </tr>
          </THead>
          <tbody>
            {rows.map((p) => {
              const [label, variant] = PAYMENT_STATUS[p.status] ?? [p.status, "muted" as const];
              const left = p.amount_cents - p.refunded_cents;
              return (
                <TR key={p.id}>
                  <TD className="num whitespace-nowrap text-xs">{formatDateTime(p.attempted_at)}</TD>
                  <TD className="max-w-[14rem] truncate">
                    <Link to="/contacts/$contactId" params={{ contactId: String(p.contact_id) }} className="hover:underline">
                      {name(p.contact_id)}
                    </Link>
                  </TD>
                  <TD className="num text-right">
                    {formatCents(p.amount_cents)}
                    {p.refunded_cents ? <div className="text-xs text-[var(--color-muted-foreground)]">{formatCents(p.refunded_cents)} refunded</div> : null}
                  </TD>
                  <TD className="text-xs">
                    <PaidWith p={p} />
                  </TD>
                  <TD>
                    <span className="flex flex-wrap gap-1">
                      <Badge variant={variant}>{label}</Badge>
                      <DisputeBadge p={p} />
                    </span>
                    {p.failure_message && <div className="max-w-[16rem] text-xs text-[var(--color-muted-foreground)]">{p.failure_message}</div>}
                  </TD>
                  <TD>
                    {p.order_id ? (
                      <Link to="/billing/orders/$orderId" params={{ orderId: String(p.order_id) }} className="num text-[var(--color-primary)] hover:underline">
                        #{p.order_id}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TD>
                  {isAdmin && (
                    <TD>
                      {p.status === "succeeded" && left > 0 && !inDispute(p) && (
                        <Button size="sm" variant="ghost" onClick={() => setRefunding(p)}>
                          Refund
                        </Button>
                      )}
                    </TD>
                  )}
                </TR>
              );
            })}
          </tbody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="border-t p-2 text-center">
          <Button size="sm" variant="outline" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            Show more
          </Button>
        </div>
      )}
      {refunding && <RefundDialog payment={refunding} onClose={() => setRefunding(null)} />}
    </Card>
  );
}
